import { useCallback, useEffect, useState } from "react"

import { Button, SofiaMark } from "@coval/ui"
import { NodeSelection } from "@tiptap/pm/state"
import { useEditorState, type Editor } from "@tiptap/react"
import {
  History,
  LockKeyhole,
  LockKeyholeOpen,
  Redo2,
  Undo2,
} from "lucide-react"

import { ConfirmationDialog } from "@/components/shared/alerts/ConfirmDialog"
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges"

import { useNotebookDocument } from "../hooks/useNotebookDocument"
import { notebookCanvas, withCanvas } from "../utils/notebookCanvas"
import { updateNotebookDocument, type getNotebook } from "../utils/notebooksApi"
import {
  supportsNotebookDocumentEditing,
  type Notebook,
} from "../utils/notebookSnapshot"
import { NotebookCanvas } from "./NotebookCanvas"
import { NotebookCanvasToolbar } from "./NotebookCanvasToolbar"
import { NotebookConflictDialog } from "./NotebookConflictDialog"
import { NotebookContent } from "./NotebookContent"
import { NotebookHistoryDialog } from "./NotebookHistoryDialog"
import { NotebookMediaDialog } from "./NotebookMediaDialog"
import { NotebookPresentation } from "./NotebookPresentation"
import { NotebookPrintButton } from "./NotebookPrintButton"

export function EditableNotebookContent({
  notebook,
  organizationSlug,
  workspaceId,
  onSaved,
  saveDocument = updateNotebookDocument,
  loadNotebook,
  onLockChange,
  busy = false,
  hasUnsentMessages = false,
}: {
  notebook: Notebook
  organizationSlug: string
  workspaceId: string
  creatorLabel?: string
  onSaved: (notebook: Notebook) => void
  saveDocument?: typeof updateNotebookDocument
  loadNotebook?: typeof getNotebook
  onLockChange?: (locked: boolean) => void
  busy?: boolean
  hasUnsentMessages?: boolean
}) {
  const state = useNotebookDocument({
    notebook,
    organizationSlug,
    workspaceId,
    onSaved,
    saveDocument,
    loadNotebook,
  })
  const [editor, setEditor] = useState<Editor | null>(null)
  const [editingText, setEditingText] = useState(false)
  const textSelection = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      const selection = current?.state.selection
      return (
        !!selection &&
        (!(selection instanceof NodeSelection) || selection.node.isTextblock)
      )
    },
  })
  const [historyOpen, setHistoryOpen] = useState(false)
  const [conflictOpen, setConflictOpen] = useState(false)
  const canEdit =
    !!notebook.editing_enabled &&
    supportsNotebookDocumentEditing(notebook.document)
  const disabled = !canEdit || !!notebook.locked || busy
  const current = { ...notebook, document: state.document }
  const appearance = state.document.appearance ?? {}
  useEffect(() => {
    onLockChange?.(state.dirty || state.saving || !!state.error || historyOpen)
  }, [state.dirty, state.saving, state.error, historyOpen, onLockChange])
  useEffect(() => () => onLockChange?.(false), [onLockChange])
  const guard = useUnsavedChanges({
    hasUnsavedChanges: useCallback(
      () => state.dirty || hasUnsentMessages,
      [state.dirty, hasUnsentMessages],
    ),
    isNavigationLocked: state.saving,
  })
  const navigationDialog = (
    <ConfirmationDialog
      showConfirmDialog={guard.isModalOpen}
      title="Leave with unsaved changes?"
      description="Your unsaved document changes, queued messages and Sofia draft will be lost if you leave this notebook."
      setShowConfirmDialog={(open) => {
        if (!open) {
          guard.handleConfirmCancel()
        }
      }}
      handleConfirmChange={guard.handleConfirmDiscard}
      handleCancelChange={guard.handleConfirmCancel}
      cancelLabel="Keep editing"
      confirmLabel="Leave"
    />
  )
  if (!canEdit) {
    return (
      <>
        <NotebookContent
          notebook={notebook}
          organizationSlug={organizationSlug}
          workspaceId={workspaceId}
        />
        {navigationDialog}
      </>
    )
  }
  return (
    <div className="space-y-3">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-coval-text-muted" role="status">
          {state.error
            ? "Changes not saved"
            : state.saving
              ? "Saving…"
              : state.dirty
                ? "Unsaved changes"
                : notebook.locked
                  ? "Locked · Viewing"
                  : "All changes saved"}
          {busy ? " · Sofia is working" : ""}
        </p>
        <div className="flex flex-wrap gap-1">
          <NotebookMediaDialog
            editor={editor}
            disabled={disabled || historyOpen}
          />
          <Button
            icon
            variant="ghost"
            tooltip="Undo (⌘Z)"
            disabled={disabled || state.saving || !state.canUndo}
            onClick={state.undo}
          >
            <Undo2 />
          </Button>
          <Button
            icon
            variant="ghost"
            tooltip="Redo (⌘⇧Z)"
            disabled={disabled || state.saving || !state.canRedo}
            onClick={state.redo}
          >
            <Redo2 />
          </Button>
          <Button
            variant="ghost"
            disabled={state.dirty || state.saving || busy}
            onClick={() => setHistoryOpen(true)}
          >
            <History />
            Activity
          </Button>
          <Button
            variant="ghost"
            disabled={state.saving || busy || !!state.error}
            onClick={() => void state.save(!notebook.locked)}
          >
            {notebook.locked ? <LockKeyhole /> : <LockKeyholeOpen />}
            {notebook.locked ? "Unlock" : "Lock"}
          </Button>
          <NotebookPrintButton
            id={notebook.id}
            title={notebook.document?.title ?? notebook.title}
            disabled={state.dirty || state.saving}
          />
          <NotebookPresentation
            notebook={current}
            organizationSlug={organizationSlug}
            workspaceId={workspaceId}
            disabled={state.dirty || state.saving || busy}
          />
        </div>
      </div>
      {state.error && (
        <div
          role="alert"
          className="mx-auto flex max-w-6xl items-center gap-3 text-sm text-coval-danger-text"
        >
          <span>{state.error} Your draft is kept here.</span>
          <Button
            variant="secondary"
            onClick={() => {
              if (state.conflictDetected) {
                setConflictOpen(true)
                if (!state.conflict) {
                  void state.loadLatest()
                }
              } else {
                void state.save()
              }
            }}
            disabled={state.saving}
          >
            {state.conflictDetected ? "Review saved version" : "Retry save"}
          </Button>
        </div>
      )}
      {busy && (
        <div
          role="status"
          className="sticky top-0 z-20 mx-auto flex max-w-6xl items-center gap-3 rounded-lg border border-coval-border bg-surface-2 px-4 py-3 text-sm text-coval-text"
        >
          <SofiaMark size={24} animated className="shrink-0" />
          <div>
            <p className="font-medium">Editing paused while Sofia works</p>
            <p className="text-coval-text-muted">
              You can edit again when this response finishes. You can still
              queue another request in Sofia.
            </p>
          </div>
        </div>
      )}
      <div
        className="space-y-3"
        onFocusCapture={(event) => {
          if (editor?.view.dom.contains(event.target)) {
            setEditingText(!event.target.closest('[contenteditable="false"]'))
          }
        }}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setEditingText(false)
          }
        }}
        onPointerDownCapture={(event) => {
          if (
            event.target instanceof Element &&
            editor?.view.dom.contains(event.target)
          ) {
            setEditingText(!event.target.closest('[contenteditable="false"]'))
          }
        }}
      >
        {editingText && textSelection && !disabled && !historyOpen && (
          <NotebookCanvasToolbar
            editor={editor}
            appearance={appearance}
            onAppearance={(look) =>
              state.change(withCanvas(current, notebookCanvas(current), look))
            }
            disabled={disabled}
          />
        )}
        <NotebookCanvas
          notebook={current}
          organizationSlug={organizationSlug}
          workspaceId={workspaceId}
          editable={!disabled}
          onEditor={setEditor}
          onChange={(content) =>
            state.change(withCanvas(current, content, appearance))
          }
          onUndo={state.undo}
          onRedo={state.redo}
        />
      </div>
      <NotebookHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        notebook={notebook}
        organizationSlug={organizationSlug}
        workspaceId={workspaceId}
        onSaved={onSaved}
      />
      <NotebookConflictDialog
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
        latest={state.conflict}
        organizationSlug={organizationSlug}
        workspaceId={workspaceId}
        saving={state.saving}
        loading={state.loadingLatest}
        onUseSaved={state.useSavedVersion}
        onSaveDraft={state.saveDraftInstead}
        onRetry={state.loadLatest}
      />
      {navigationDialog}
    </div>
  )
}
